import { ForwardedRef, forwardRef, useState } from 'react';

import { Box, Heading, Text } from '@chakra-ui/react';
import { headerHeight } from '@udacity/udacity-header-v2';

import { Card } from '~/components/card';
import { SanityImage } from '~/components/sanity-image';
import { TemplateForm } from '~/components/template-form';
import { useLeadFormEvent } from '~/features/analytics/hooks/use-lead-form-event';
import { LeadCaptureType } from '~/features/enterprise-resource/models/shared-models';
import { TemplateForm as TemplateFormType } from '~/features/page-template/models/template-form';
import { useUser } from '~/features/user/hooks/use-user';
import { transformFormDataToEventProperties } from '~/utils/transform-form-data-to-event-properties';

import { CaseStudyFormDescription } from '../models/case-study-content';

export type CaseStudyFormProps = {
  templateForm: TemplateFormType;
  formDescription: CaseStudyFormDescription;
  fileUrl?: string;
  leadCaptureType: LeadCaptureType | null;
};

export function CaseStudyFormComponent(
  { formDescription, templateForm, fileUrl, leadCaptureType }: CaseStudyFormProps,
  ref?: ForwardedRef<HTMLDivElement>
) {
  const { user } = useUser();
  const { sendSegmentLeadFormEvents, isSubmitting } = useLeadFormEvent();
  const [hasSubmittedForm, setHasSubmittedForm] = useState(false);

  async function handleSubmit({ formData }: { templateForm: TemplateFormType; formData: any }) {
    const properties = {
      ...transformFormDataToEventProperties({
        ...formData,
      }),
      ...(!formData.workEmail && formData.email && { work_email: formData.email }), // Note: work_email is necessary for the event to be sent to Marketo correctly as work_email is used as a primary key.
    };
    const sendToSalesforce = templateForm.sendToSalesforce ?? undefined;
    const identifyLabel = leadCaptureType === 'B2C' ? 'Consumer Lead' : `BD Interested - Enterprise Lead - HQ`;
    const eventName = leadCaptureType === 'B2C' ? 'Marketing Qualified Lead Form Submitted' : 'Enterprise Lead';

    setHasSubmittedForm(true);

    await sendSegmentLeadFormEvents({
      identifyTraits: {
        ...properties,
        // Note: the label here is necessary for the event to be sent to Marketo.
        // See: https://app.segment.com/udacity/destinations/marketo-v2/sources/frontend-endor/instances/5f5017123c6d5f1f65e281ce/filters
        label: identifyLabel,
      },
      trackAnalyticsEvent: {
        name: eventName,
        type: 'submit',
        label: 'case study',
        action: 'form_submitted',
        additionalAttributes: properties,
      },
      integrations: { sendToSalesforce },
    });

    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  }

  const email = user?.email ?? '';

  return (
    <Box
      ref={ref}
      display='flex'
      flexDirection={{ base: 'column', lg: 'row' }}
      position='relative'
      scrollMarginTop={headerHeight}
      alignItems={{
        lg: 'center',
      }}
      gap={{
        base: '40px',
        lg: '80px',
      }}
    >
      <Box
        flex={{
          lg: '0 1 650px',
        }}
      >
        <Box
          bgColor='#D0DEF2'
          borderRadius='8px'
          position='relative'
          height={{
            base: '224px',
            lg: '550px',
          }}
          margin={{
            sm: '0 auto',
            lg: 'unset',
          }}
          maxWidth={{
            base: '100%',
            sm: '280px',
            lg: 'unset',
          }}
          width={{
            base: '100%',
            lg: '400px',
          }}
        >
          <Box
            left='50%'
            pointerEvents='none'
            position='absolute'
            top='50%'
            transform='translate(-50%, -50%)'
            height={{
              base: '364px',
              lg: '900px',
            }}
            width={{
              base: '100%',
              lg: '688.50px',
            }}
          >
            <SanityImage image={formDescription.image} quality={80} style={{ objectFit: 'contain' }} fill />
          </Box>
        </Box>
      </Box>
      <Box display='flex' flexDirection='column' gap='40px' position='relative'>
        <Box
          textAlign={{
            base: 'center',
            lg: 'left',
          }}
        >
          <Heading
            as='p'
            color='blue.400'
            fontWeight='500 !important'
            mb={{
              base: 2,
              lg: 4,
            }}
            size={{
              base: 'marketing-4xl',
              lg: 'marketing-5xl',
            }}
          >
            {formDescription.title}
          </Heading>

          <Text>{formDescription.markdownContent}</Text>
        </Box>

        <Card
          border='none'
          borderRadius='8px'
          position='relative'
          zIndex='2'
          padding={{
            base: '24px',
            lg: '40px',
          }}
          paddingTop={{
            lg: '32px',
          }}
        >
          <Box>
            <TemplateForm
              isDisabled={hasSubmittedForm}
              isFullWidth={true}
              templateForm={templateForm}
              defaultValues={{
                email,
              }}
              formFieldElementsWrapper={
                <Box
                  display='flex'
                  flexDir='column'
                  mb={8}
                  gap={{
                    base: 4,
                    lg: 6,
                  }}
                />
              }
              renderFormFields={(formFields) => {
                return (
                  <>
                    <Box
                      display='flex'
                      flex='1'
                      flexDir={{
                        base: 'column',
                        lg: 'row',
                      }}
                      gap={{
                        base: 4,
                        lg: 6,
                      }}
                    >
                      {formFields[0]}
                      {formFields[1]}
                    </Box>
                    {formFields.slice(2)}
                  </>
                );
              }}
              submitButtonProps={{
                marginLeft: 'auto',
                marginRight: 'auto',
                isLoading: isSubmitting,
                width: '100%',
              }}
              onSubmit={handleSubmit}
            />
          </Box>
        </Card>
      </Box>
    </Box>
  );
}

export const CaseStudyForm = forwardRef(CaseStudyFormComponent);
